import { useEffect, useRef, useState } from 'react';
import type { User } from '@capacitor-firebase/authentication';
import { ensureAuthenticated, getCurrentUser, subscribeAuth } from '@/services/AuthService';
import { registerFcmToken } from '@/features/notifications/fcmRegistration';
import { useAuthStore, type AuthMode } from '@/stores/authStore';
// NOTE: Firestore sync start/stop is NOT driven from here. It is owned by the
// JS-auth controller in AuthService, which keys off the JS SDK identity that
// Firestore actually authenticates with. Driving it from the native auth state
// here raced ahead of the JS-auth bridge and got permission-denied on the first
// read (see AuthService onAuthStateChanged).

export type AuthState =
  | { status: 'loading'; user: null; error: null; authMode: 'anonymous' }
  | { status: 'ready'; user: User; error: null; authMode: AuthMode }
  | { status: 'error'; user: null; error: Error; authMode: 'anonymous' };

export const useAuth = (): AuthState => {
  const initial = getCurrentUser();
  const [state, setState] = useState<AuthState>(
    initial
      ? { status: 'ready', user: initial, error: null, authMode: useAuthStore.getState().authMode }
      : { status: 'loading', user: null, error: null, authMode: 'anonymous' },
  );
  const fcmRegistered = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const unsub = subscribeAuth((user) => {
      if (cancelled) return;
      if (user) {
        const authMode = useAuthStore.getState().authMode;
        setState({ status: 'ready', user, error: null, authMode });
        if (!fcmRegistered.current) {
          fcmRegistered.current = true;
          void registerFcmToken(user.uid);
        }
      }
    });

    ensureAuthenticated().catch((err: unknown) => {
      if (cancelled) return;
      setState({
        status: 'error',
        user: null,
        error: err instanceof Error ? err : new Error(String(err)),
        authMode: 'anonymous',
      });
    });

    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  return state;
};
